/**
 * Admin Management API Client
 * 
 * Typed client for the /admin/auth and /management endpoints.
 */

// ============================================================================
// Types
// ============================================================================

export interface ServerStatus {
    uptime_seconds: number;
    version: string;
    git_commit: string;
    python_version: string;
    platform: string;
    memory_mb: number;
    memory_percent: number;
    cpu_percent: number;
    features: Record<string, boolean>;
    active_sessions: number;
}

export interface StorageInfo {
    disk: {
        total_gb: number;
        free_gb: number;
        used_percent: number;
    };
    storage_dir_mb: number;
    databases: Array<{ name: string; size_mb: number }>;
    media_count: number;
    documents_count: number;
}

export interface GitInfo {
    branch: string;
    commit_hash: string;
    commit_message: string;
    commit_date: string;
    uncommitted_changes: number;
    remote_url: string;
    behind_remote?: number;
}

export interface LogEntry {
    timestamp: string;
    timestamp_unix: number;
    level: 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
    logger: string;
    message: string;
    extra?: Record<string, unknown>;
}

export interface LogQuery {
    level?: string;
    logger_name?: string;
    since?: number;
    until?: number;
    limit?: number;
    offset?: number;
    include_persisted?: boolean;
}

export interface LogResponse {
    logs: LogEntry[];
    stats: {
        buffer_used: number;
        total_entries: number;
    };
}

export interface TraceEntry {
    index: number;
    timestamp: string;
    provider: string;
    model: string;
    duration_ms: number;
    success: boolean;
    prompt_preview: string;
    response_preview: string;
    label?: string;
}

export interface ChallengeResponse {
    challenge_id: string;
    expires_at: string;
}

export interface VerifyResponse {
    admin_token: string;
    user_id: string;
    expires_at: string;
}

export interface DeployResult {
    success: boolean;
    git_output?: string;
    restarting?: boolean;
    error?: string;
}

// Organization Types
export interface OrgInfo {
    id: string;
    name: string;
    slug: string;
    owner_id: string;
    plan: string;
    max_seats: number;
    logo_url?: string;
    created_at: string;
    updated_at: string;
    member_count?: number;
}

export interface OrgMember {
    user_id: string;
    role: string;
    invited_by?: string;
    joined_at: string;
}

export interface OrgInvitation {
    id: string;
    email: string;
    role: string;
    invited_by: string;
    invited_at: string;
    expires_at: string;
    /**
     * Raw accept token, present ONLY on the response that creates the
     * invitation. `id` is not a credential: it used to double as the accept
     * token, so the invitation listing handed every org admin a redeemable
     * bearer value for every pending invite. The listing never returns this.
     */
    token?: string;
}

export interface OrgPolicies {
    require_2fa: boolean;
    allowed_providers?: string[];
    data_residency?: string;
    max_storage_gb?: number;
    max_files_per_project?: number;
    updated_at: string;
}

// ============================================================================
// API Client
// ============================================================================

class AdminAPI {
    private baseUrl: string;
    private token: string | null = null;

    constructor(baseUrl?: string) {
        if (baseUrl !== undefined) {
            this.baseUrl = baseUrl;
        } else {
            // Auto-detect: in dev, Vite proxy handles /admin and /management routes.
            // In production (Cloudflare Pages), the API lives on a different origin.
            const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            this.baseUrl = isDev ? '' : 'https://api.isneps.com';
        }
    }

    // --------------------------------------------------------------------------
    // Token Management
    // --------------------------------------------------------------------------

    setToken(token: string | null): void {
        this.token = token;
        if (token) {
            localStorage.setItem('admin_token', token);
        } else {
            localStorage.removeItem('admin_token');
        }
    }

    getToken(): string | null {
        if (!this.token) {
            this.token = localStorage.getItem('admin_token');
        }
        return this.token;
    }

    isAuthenticated(): boolean {
        return !!this.getToken();
    }

    // --------------------------------------------------------------------------
    // HTTP Helpers
    // --------------------------------------------------------------------------

    private async request<T>(
        method: string,
        path: string,
        body?: unknown,
        requireAuth = true
    ): Promise<T> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (requireAuth) {
            const token = this.getToken();
            if (!token) {
                throw new Error('Not authenticated');
            }
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${this.baseUrl}${path}`, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
        });

        if (response.status === 401) {
            this.setToken(null);
            throw new Error('Session expired');
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || `Request failed: ${response.status}`);
        }

        return response.json();
    }

    // --------------------------------------------------------------------------
    // Authentication
    // --------------------------------------------------------------------------

    async requestChallenge(phone: string): Promise<ChallengeResponse> {
        return this.request<ChallengeResponse>(
            'POST',
            '/admin/auth/request',
            { phone },
            false
        );
    }

    async verifyChallenge(challengeId: string, code: string): Promise<VerifyResponse> {
        const result = await this.request<VerifyResponse>(
            'POST',
            '/admin/auth/verify',
            { challenge_id: challengeId, code },
            false
        );
        this.setToken(result.admin_token);
        return result;
    }

    async logout(): Promise<void> {
        try {
            await this.request('POST', '/admin/auth/logout');
        } finally {
            this.setToken(null);
        }
    }

    /**
     * Exchange an Anubis API key for an admin session token (SSO flow).
     * Called when navigating from the Anubis webapp with hash-fragment credentials.
     */
    async exchangeToken(apiKey: string): Promise<VerifyResponse> {
        const response = await fetch(`${this.baseUrl}/admin/auth/exchange`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`,
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || `Token exchange failed: ${response.status}`);
        }

        const result: VerifyResponse = await response.json();
        this.setToken(result.admin_token);
        return result;
    }

    // --------------------------------------------------------------------------
    // Status & Monitoring
    // --------------------------------------------------------------------------

    async getHealth(): Promise<{ status: string; timestamp: string }> {
        return this.request('GET', '/management/health');
    }

    async getStatus(): Promise<ServerStatus> {
        return this.request('GET', '/management/status');
    }

    async getStorage(): Promise<StorageInfo> {
        return this.request('GET', '/management/storage');
    }

    async getConfig(): Promise<Record<string, unknown>> {
        return this.request('GET', '/management/config');
    }

    // --------------------------------------------------------------------------
    // Logs
    // --------------------------------------------------------------------------

    async getLogs(query: LogQuery = {}): Promise<LogResponse> {
        const params = new URLSearchParams();
        if (query.level) params.set('level', query.level);
        if (query.logger_name) params.set('logger_name', query.logger_name);
        if (query.since) params.set('since', query.since.toString());
        if (query.until) params.set('until', query.until.toString());
        if (query.limit) params.set('limit', query.limit.toString());
        if (query.offset) params.set('offset', query.offset.toString());
        if (query.include_persisted) params.set('include_persisted', 'true');

        const queryString = params.toString();
        const path = `/management/logs${queryString ? `?${queryString}` : ''}`;
        return this.request('GET', path);
    }

    // --------------------------------------------------------------------------
    // Traces
    // --------------------------------------------------------------------------

    async getTraces(limit = 20): Promise<{ traces: TraceEntry[]; total: number }> {
        return this.request('GET', `/management/traces?limit=${limit}`);
    }

    async getTrace(index: number): Promise<unknown> {
        return this.request('GET', `/management/traces/${index}`);
    }

    // --------------------------------------------------------------------------
    // Git & Updates
    // --------------------------------------------------------------------------

    async getGitStatus(): Promise<GitInfo> {
        return this.request('GET', '/management/git/status');
    }

    async gitPull(): Promise<{ success: boolean; stdout?: string; stderr?: string }> {
        return this.request('POST', '/management/git/pull');
    }

    async deploy(): Promise<DeployResult> {
        return this.request('POST', '/management/deploy');
    }

    async restart(): Promise<{ restarting: boolean; message: string }> {
        return this.request('POST', '/management/restart');
    }

    // --------------------------------------------------------------------------
    // Tests
    // --------------------------------------------------------------------------

    async listTests(): Promise<{ tests: string[]; categories: Record<string, string[]> }> {
        return this.request('GET', '/management/tests/list');
    }

    async runTests(test?: string): Promise<{ passed: number; failed: number; output: string }> {
        return this.request('POST', '/management/tests/run', test ? { test } : {});
    }

    // --------------------------------------------------------------------------
    // Organizations
    // --------------------------------------------------------------------------

    async getOrganizations(): Promise<{ organizations: OrgInfo[] }> {
        return this.request('GET', '/organizations');
    }

    async createOrganization(name: string, plan: string = 'free'): Promise<OrgInfo> {
        const maxSeats = plan === 'enterprise' ? 100 : plan === 'pro' ? 25 : 5;
        return this.request('POST', '/organizations', { name, plan, max_seats: maxSeats });
    }

    async getOrganization(orgId: string): Promise<OrgInfo> {
        return this.request('GET', `/organizations/${orgId}`);
    }

    async deleteOrganization(orgId: string): Promise<{ deleted: boolean }> {
        return this.request('DELETE', `/organizations/${orgId}`);
    }

    // Members
    async getMembers(orgId: string): Promise<{ members: OrgMember[] }> {
        return this.request('GET', `/organizations/${orgId}/members`);
    }

    async updateMemberRole(orgId: string, userId: string, role: string): Promise<{ updated: boolean }> {
        return this.request('PATCH', `/organizations/${orgId}/members/${userId}`, { role });
    }

    async removeMember(orgId: string, userId: string): Promise<{ removed: boolean }> {
        return this.request('DELETE', `/organizations/${orgId}/members/${userId}`);
    }

    // Invitations
    async inviteMember(orgId: string, email: string, role: string = 'member'): Promise<OrgInvitation> {
        return this.request('POST', `/organizations/${orgId}/invite`, { email, role });
    }

    async getInvitations(orgId: string): Promise<{ invitations: OrgInvitation[] }> {
        return this.request('GET', `/organizations/${orgId}/invitations`);
    }

    async revokeInvitation(orgId: string, inviteId: string): Promise<{ revoked: boolean }> {
        return this.request('DELETE', `/organizations/${orgId}/invitations/${inviteId}`);
    }

    // Policies
    async getPolicies(orgId: string): Promise<OrgPolicies> {
        return this.request('GET', `/organizations/${orgId}/policies`);
    }

    async updatePolicies(orgId: string, policies: Partial<OrgPolicies>): Promise<OrgPolicies> {
        return this.request('PUT', `/organizations/${orgId}/policies`, policies);
    }
}

// Singleton instance
export const api = new AdminAPI();
